import { CellClickedEvent, GridOptions } from 'ag-grid-community';
import React, { ComponentType, FC, useCallback } from 'react';
import { setDisplayName, wrapDisplayName } from 'recompose';

type KnownProps = Pick<
  GridOptions,
  | 'suppressClickEdit'
  | 'stopEditingWhenGridLosesFocus'
  | 'enterMovesDownAfterEdit'
  | 'onCellClicked'
>;

type CellClickedHandler = (event: CellClickedEvent) => boolean;

export type SingleClickEditProps = {
  onCellClick?: CellClickedHandler;
};

const isEditable = (event: CellClickedEvent) => {
  const { api, colDef, column, columnApi, context, data, node } = event;
  const { editable } = colDef;

  if (editable == null) {
    return false;
  }

  return typeof editable === 'boolean'
    ? editable
    : editable({ api, colDef, column, columnApi, context, data, node });
};

export const startEditing = ({
  api,
  column,
  rowIndex,
}: Pick<CellClickedEvent, 'api' | 'column' | 'rowIndex'>) => {
  api.startEditingCell({
    colKey: column,
    rowIndex,
  });
};

export const withSingleClickEdit = <T extends KnownProps>(
  Component: ComponentType<T>,
): FC<Omit<T, keyof KnownProps> & SingleClickEditProps> => {
  const Next: FC<Omit<T, keyof KnownProps> & SingleClickEditProps> = ({
    onCellClick: onClicked,
    ...props
  }) => {
    const onCellClicked = useCallback(
      (event: CellClickedEvent) => {
        const handled = onClicked?.(event) ?? false;

        if (!handled && isEditable(event)) {
          startEditing(event);
        }
      },
      [onClicked],
    );

    return (
      <Component
        {...(props as any)}
        suppressClickEdit
        // stopEditingWhenGridLosesFocus
        enterMovesDownAfterEdit
        onCellClicked={onCellClicked}
      />
    );
  };

  return setDisplayName(wrapDisplayName(Component, 'WithSingleClickEdit'))(
    Next,
  );
};
